import React, { useState, useEffect, useCallback } from 'react';

function WordForm({ onAddWord, todaysWords, maxWords }) {
  const [currentWord, setCurrentWord] = useState({ spanish: '', english: '' });
  const [isTranslating, setIsTranslating] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const wordCount = todaysWords.length;

  // Debounced translation function
  const debouncedTranslate = useCallback((text) => {
    // Clear any existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set a new timeout
    const timeoutId = setTimeout(async () => {
      if (text.trim() !== '') {
        try {
          setIsTranslating(true);
          const translation = await translateText(text);
          setCurrentWord(prev => ({ ...prev, english: translation }));
        } catch (error) {
          console.error('Translation error:', error);
        } finally {
          setIsTranslating(false);
        }
      }
    }, 800); // Wait 800ms after typing stops before translating

    setTypingTimeout(timeoutId);
  }, [typingTimeout]);

  const handleChange = (value) => {
    // Only update the Spanish word immediately
    setCurrentWord(prev => ({ ...prev, spanish: value }));
    
    // If the field is empty, clear the translation
    if (!value.trim()) {
      setCurrentWord(prev => ({ ...prev, english: '' }));
      return;
    }
    
    // Otherwise, trigger the debounced translation
    debouncedTranslate(value);
  };

  const translateText = async (text) => {
    try {
      // Using MyMemory Translation API (free, no auth required for limited usage)
      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=es|en`
      );
      
      if (!response.ok) {
        throw new Error('Translation request failed');
      }
      
      const data = await response.json();
      return data.responseData.translatedText;
    } catch (error) {
      console.error('Translation API error:', error);
      return ''; // Return empty string if translation fails
    }
  };

  const handleAddWord = async (e) => {
    e.preventDefault();
    
    if (currentWord.spanish && currentWord.english) {
      setIsSubmitting(true);
      
      try {
        const success = await onAddWord(currentWord);
        if (success) {
          setCurrentWord({ spanish: '', english: '' });
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      alert('Please enter a Spanish word');
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [typingTimeout]);

  return (
    <div className="word-form">
      <h2>Add Today's Words ({wordCount}/{maxWords})</h2>
      
      <form onSubmit={handleAddWord}>
        <div className="word-input-group">
          <input
            type="text"
            placeholder="Enter Spanish word"
            value={currentWord.spanish}
            onChange={(e) => handleChange(e.target.value)}
            className="spanish-input"
            disabled={isSubmitting}
          />
          
          <div className="translation-display">
            <label>English Translation:</label>
            <div className={`translation-text ${isTranslating ? "translating" : ""}`}>
              {isTranslating ? "Translating..." : currentWord.english || "Translation will appear here"}
            </div>
          </div>
          
          <button 
            type="submit" 
            disabled={isTranslating || isSubmitting || !currentWord.spanish || !currentWord.english}
          >
            {isSubmitting ? "Adding..." : "Add Word"}
          </button>
        </div>
      </form>
      
      {todaysWords.length > 0 && (
        <div className="added-words">
          <h3>Words added today:</h3>
          <ul>
            {todaysWords.map((word, index) => (
              <li key={index}>
                <span className="spanish">{word.spanish}</span> - 
                <span className="english">{word.english}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default WordForm; 